import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArrowLeft,
  CalendarClock,
  Info,
  Loader2,
  Play,
  ShieldAlert,
} from "lucide-react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { retentionApi } from "@/services/api";

// Page follows the Certificates settings precedent: English-only copy.
const DATA_TYPES = {
  completed_envelopes: {
    label: "Completed envelopes",
    hint: "Fully signed envelopes, their documents and audit trail",
  },
  draft_envelopes: {
    label: "Draft envelopes",
    hint: "Envelopes that were never sent",
  },
  voided_envelopes: {
    label: "Voided envelopes",
    hint: "Envelopes voided before completion",
  },
  audit_logs: {
    label: "Audit logs",
    hint: "Organisation and system audit events not attached to a live envelope, processed in monthly batches (envelope audit trails follow their envelope's policy)",
    // Batches always cover fully-expired months; the applies-to-existing
    // distinction doesn't apply
    hideAppliesToExisting: true,
  },
  user_data_after_deletion: {
    label: "User data after account deletion",
    hint: "How long after an account is deleted its residual traces (audit entries, guest-saved signatures) are anonymized",
    hideAppliesToExisting: true,
  },
};

const ACTIONS = [
  { value: "archive", label: "Archive" },
  { value: "anonymize", label: "Anonymize (remove personal data)" },
  { value: "delete", label: "Delete permanently" },
];

// Kept in sync with DataRetentionService.ALLOWED_ACTIONS on the backend:
// audit logs are integrity-chained legal records (no delete); residual
// data of deleted users is anonymized by construction.
const TYPE_ACTIONS = {
  audit_logs: ["archive", "anonymize"],
  user_data_after_deletion: ["anonymize"],
};

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  processing: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  completed: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  held: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
  failed: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

const formatPeriod = (days) => {
  const n = Number(days);
  if (!n || n < 1) return "";
  if (n % 365 === 0) return `${n / 365} year${n / 365 === 1 ? "" : "s"}`;
  if (n >= 365) return `≈ ${(n / 365).toFixed(1)} years`;
  return `${n} days`;
};

function PolicyCard({ policy, onSave, saving }) {
  const meta = DATA_TYPES[policy.dataType] || { label: policy.dataType };
  const allowedActions = TYPE_ACTIONS[policy.dataType] || null;
  const actions = allowedActions
    ? ACTIONS.filter((a) => allowedActions.includes(a.value))
    : ACTIONS;
  const [form, setForm] = useState({
    isEnabled: policy.isEnabled,
    retentionPeriodDays: policy.retentionPeriodDays,
    action: policy.action,
    appliesToExisting: policy.appliesToExisting,
  });

  const dirty =
    form.isEnabled !== policy.isEnabled ||
    Number(form.retentionPeriodDays) !== policy.retentionPeriodDays ||
    form.action !== policy.action ||
    form.appliesToExisting !== policy.appliesToExisting;

  const daysInvalid =
    !Number.isInteger(Number(form.retentionPeriodDays)) ||
    Number(form.retentionPeriodDays) < 1 ||
    Number(form.retentionPeriodDays) > 36500;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">{meta.label}</CardTitle>
            <CardDescription>{meta.hint}</CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Label
              htmlFor={`enabled-${policy.id}`}
              className="text-sm text-muted-foreground"
            >
              {form.isEnabled ? "Enabled" : "Disabled"}
            </Label>
            <Switch
              id={`enabled-${policy.id}`}
              checked={form.isEnabled}
              onCheckedChange={(checked) =>
                setForm((f) => ({ ...f, isEnabled: checked }))
              }
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor={`days-${policy.id}`}>Retention period (days)</Label>
            <Input
              id={`days-${policy.id}`}
              type="number"
              min={1}
              max={36500}
              value={form.retentionPeriodDays}
              onChange={(e) =>
                setForm((f) => ({ ...f, retentionPeriodDays: e.target.value }))
              }
            />
            <p className="text-xs text-muted-foreground">
              {formatPeriod(form.retentionPeriodDays)}
            </p>
          </div>
          <div className="space-y-2">
            <Label>Action when the period ends</Label>
            <Select
              value={form.action}
              onValueChange={(value) =>
                setForm((f) => ({ ...f, action: value }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {actions.map((a) => (
                  <SelectItem key={a.value} value={a.value}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!meta.hideAppliesToExisting && (
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="pr-4">
              <p className="text-sm font-medium">Apply to existing items</p>
              <p className="text-xs text-muted-foreground">
                Also schedule items that already existed before this policy
                was created. Off: only items created after the policy counts
                down.
              </p>
            </div>
            <Switch
              checked={form.appliesToExisting}
              onCheckedChange={(checked) =>
                setForm((f) => ({ ...f, appliesToExisting: checked }))
              }
            />
          </div>
        )}

        {form.isEnabled && form.action === "delete" && (
          <div className="flex gap-2 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg text-sm text-red-800 dark:text-red-300">
            <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
            <p>
              Deletion is permanent. Files are removed from storage and a
              deletion certificate is issued for each item. Items under an
              active legal hold are always skipped.
            </p>
          </div>
        )}

        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={!dirty || daysInvalid || saving}
            onClick={() =>
              onSave({
                dataType: policy.dataType,
                name: policy.name,
                description: policy.description,
                retentionPeriodDays: Number(form.retentionPeriodDays),
                action: form.action,
                isEnabled: form.isEnabled,
                appliesToExisting: form.appliesToExisting,
              })
            }
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Save policy
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DataRetentionSettings() {
  const queryClient = useQueryClient();
  const [schedulePage, setSchedulePage] = useState(1);

  const { data: policies, isLoading: policiesLoading } = useQuery({
    queryKey: ["retention-policies"],
    queryFn: () =>
      retentionApi.getPolicies().then((r) => r.data?.data?.policies || []),
  });

  const { data: schedule, isLoading: scheduleLoading } = useQuery({
    queryKey: ["retention-schedule", schedulePage],
    queryFn: () =>
      retentionApi
        .getSchedule({ page: schedulePage, limit: 10 })
        .then((r) => r.data?.data),
  });

  const refetchAll = () => {
    queryClient.invalidateQueries({ queryKey: ["retention-policies"] });
    queryClient.invalidateQueries({ queryKey: ["retention-schedule"] });
  };

  const initializeMutation = useMutation({
    mutationFn: () => retentionApi.initializeDefaults(),
    onSuccess: () => {
      toast.success(
        "Default policies created. They start disabled — review and enable the ones you want.",
      );
      refetchAll();
    },
    onError: (error) =>
      toast.error(
        error.response?.data?.error?.message || "Failed to create policies",
      ),
  });

  const upsertMutation = useMutation({
    mutationFn: (data) => retentionApi.upsertPolicy(data),
    onSuccess: () => {
      toast.success("Retention policy saved");
      refetchAll();
    },
    onError: (error) =>
      toast.error(
        error.response?.data?.error?.message || "Failed to save policy",
      ),
  });

  const processMutation = useMutation({
    mutationFn: () => retentionApi.processDueItems(),
    onSuccess: (res) => {
      const processed = res.data?.data?.processed ?? 0;
      toast.success(
        processed > 0
          ? `Processed ${processed} due item${processed === 1 ? "" : "s"}`
          : "No items were due for processing",
      );
      refetchAll();
    },
    onError: (error) =>
      toast.error(
        error.response?.data?.error?.message || "Failed to process items",
      ),
  });

  const scheduleItems = schedule?.items || [];
  const scheduleTotal = schedule?.total || 0;
  const totalPages = Math.max(1, Math.ceil(scheduleTotal / 10));

  let policiesContent;
  if (policiesLoading) {
    policiesContent = (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  } else if (!policies?.length) {
    policiesContent = (
      <Card>
        <CardContent className="py-12 text-center">
          <Archive className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-lg font-semibold mb-1">
            No retention policies yet
          </h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
            Create the default set of policies (completed, draft and voided
            envelopes, audit logs, deleted-user data). They are created
            disabled so nothing happens until you review and enable them.
          </p>
          <Button
            onClick={() => initializeMutation.mutate()}
            disabled={initializeMutation.isPending}
          >
            {initializeMutation.isPending && (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            )}
            Create default policies
          </Button>
        </CardContent>
      </Card>
    );
  } else {
    policiesContent = (
      <div className="grid gap-4">
        {policies.map((policy) => (
          <PolicyCard
            key={policy.id}
            policy={policy}
            saving={upsertMutation.isPending}
            onSave={(data) => upsertMutation.mutate(data)}
          />
        ))}
      </div>
    );
  }

  let scheduleContent;
  if (scheduleLoading) {
    scheduleContent = (
      <div className="flex justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  } else if (scheduleItems.length === 0) {
    scheduleContent = (
      <p className="text-sm text-muted-foreground text-center py-8">
        Nothing scheduled yet. Items appear here once an enabled policy picks
        them up.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header with back link */}
      <div className="flex items-center gap-4">
        <Link
          to="/settings"
          className="flex items-center justify-center h-8 w-8 rounded-lg border border-border hover:bg-accent transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">Data Retention</h1>
          <p className="text-muted-foreground">
            Automatically archive, anonymize or delete data after a period you
            choose
          </p>
        </div>
      </div>

      {/* How it works */}
      <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex gap-3">
          <div className="shrink-0">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
              <Info className="w-4 h-4 text-primary" />
            </div>
          </div>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>
              Enabled policies run automatically every night. When an item's
              retention period ends it is scheduled, then archived, anonymized
              or deleted on its due date. Items under an active legal hold are
              never touched, and every deletion issues a certificate for your
              compliance records.
            </p>
            <p>
              Policies apply to this organisation only and start out disabled.
            </p>
          </div>
        </div>
      </div>

      {/* Policies */}
      {policiesContent}

      {/* Scheduled items */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarClock className="w-4 h-4" />
                Scheduled items
              </CardTitle>
              <CardDescription>
                Items queued by your policies and what happened to them
                {scheduleTotal > 0 && ` (${scheduleTotal} total)`}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => processMutation.mutate()}
              disabled={processMutation.isPending}
            >
              {processMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Process due items now
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {scheduleContent || (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">Item</th>
                      <th className="py-2 pr-4 font-medium">Action</th>
                      <th className="py-2 pr-4 font-medium">Due date</th>
                      <th className="py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduleItems.map((item) => (
                      <tr key={item.id} className="border-b border-border/50">
                        <td className="py-2 pr-4">
                          <p className="font-medium truncate max-w-55">
                            {item.resourceName || item.resourceId}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {DATA_TYPES[item.dataType]?.label ||
                              item.resourceType}
                          </p>
                        </td>
                        <td className="py-2 pr-4 capitalize">
                          {item.scheduledAction}
                        </td>
                        <td className="py-2 pr-4">
                          {item.scheduledDate
                            ? new Date(item.scheduledDate).toLocaleDateString()
                            : "—"}
                        </td>
                        <td className="py-2">
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              STATUS_STYLES[item.status] || "bg-muted"
                            }`}
                            title={item.errorMessage || undefined}
                          >
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-end gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={schedulePage <= 1}
                    onClick={() => setSchedulePage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {schedulePage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={schedulePage >= totalPages}
                    onClick={() => setSchedulePage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
